// TS types
import { ICommonStore } from '@redux/Common/interfaces';
import { IGDSStore } from '@redux/GDS/interfaces';
import { scrollToRef, IOnSubmit, IValidateItem } from '@redux/common';
// Redux
import { store } from '@redux/store';
// api
import getEnvConfig from '@api/config';
// common function
import {
    formatProductDetailOrder,
    formatPassengerURL,
    openMemberLightBox,
    manageSessionStorage,
    parseApiResponse,
} from '@libs/core';
import { isMobile } from '@libs/globalVariables';
import { fetchProductOrderValidity } from '@libs/apiCall';
// plugin
import dayjs from 'dayjs';
import { t } from 'i18next';
// redux actions
import {
    setGCalenderErr,
    setGSessionErr,
    setGTicketErr,
    setScrollTo,
    setSubmitWatcher,
    setIsSubmitSuccess,
} from '@redux/GDS/actions';
import { setVerificationStatus } from '@redux/Common/actions';

const onSubmit: IOnSubmit = async (type) => {
    const dispatch = store.dispatch;
    const allStates = store.getState();
    const submitURL = getEnvConfig().GDS.submitURL;
    const {
        calendar,
        ticketNum,
        GDetail,
        GNowListData,
        GNowList,
        sessionPicked,
        totalAmount,
        submitWatcher,
    }: IGDSStore = allStates.GDS;
    const {
        ETID,
        Foreign,
        isLogin,
        isErpOrder,
        GListData: { AdvanceTime },
        ProductDescKey,
    }: ICommonStore = allStates.Common;
    const hasCalendar = GNowListData?.ExpectedUseDay;
    const hasSession = GDetail?.Sessions;
    const submitDate = hasCalendar ? calendar : dayjs().format('YYYY-MM-DD');

    const session =
        hasSession && sessionPicked
            ? sessionPicked
            : {
                  Session: '',
                  SessionUUID: '',
              };

    switch (type) {
        case 'checkData':
            dispatch(setSubmitWatcher(!submitWatcher));

            const submitObj = {
                calendar: calendar,
                session: session,
                ticket: ticketNum,
                totalAmount: totalAmount,
                ETID: ETID,
                FormatID: GNowList,
                submiturl: submitURL,
                URLFOREIGN: Foreign,
            };
            const calendarErr = ((data: string) => {
                if (!data) return true;
                return false;
            })(submitObj.calendar);
            const sessionErr = ((data: any) => {
                if (hasSession && data.Session === '') return true;
                return false;
            })(submitObj.session);
            const ticketErr = ((data) => {
                if (!data) return true;
                const ticketVals = Object.values(data);
                if (typeof ticketVals === 'object' && ticketVals.length === 0) return true;
                let headCount = ticketVals.reduce((a: number, b: number) => a + b);
                if (headCount <= 0) return true;
                if (GDetail?.ETItemLowQty && headCount < GDetail?.ETItemLowQty) return 'GFailLow';
                if (GDetail?.ETItemHighQty && headCount > GDetail?.ETItemHighQty)
                    return 'GFailHigh';
                if (GDetail?.ETItemMutiple && headCount % GDetail?.ETItemMutiple !== 0)
                    return 'GFailMutiple';
                return false;
            })(submitObj.ticket);
            if (hasCalendar && calendarErr) {
                dispatch(setGCalenderErr(t('noTHSR.onsubmit.selectDepartureDate')));
            }
            if (sessionErr) {
                dispatch(setGSessionErr(t('noTHSR.onsubmit.selectSessionTime')));
            }
            if (ticketErr) {
                if (ticketErr === 'GFailHigh') {
                    dispatch(
                        setGTicketErr(
                            t('noTHSR.onsubmit.maxBookingQuantity', {
                                highQty: GDetail?.ETItemHighQty,
                            })
                        )
                    );
                } else if (ticketErr === 'GFailMutiple') {
                    dispatch(
                        setGTicketErr(
                            t('noTHSR.onsubmit.multipleOfNum', {
                                multipleQty: GDetail?.ETItemMutiple,
                            })
                        )
                    );
                } else if (ticketErr === 'GFailLow') {
                    //會有最低數量小於倍數的情況時，最低數量要改成最低倍數
                    if (GDetail?.ETItemMutiple > GDetail?.ETItemLowQty) {
                        dispatch(
                            setGTicketErr(
                                t('noTHSR.onsubmit.minBookingQuantity', {
                                    minQty: GDetail?.ETItemMutiple,
                                })
                            )
                        );
                    } else {
                        dispatch(
                            setGTicketErr(
                                t('noTHSR.onsubmit.minBookingQuantity', {
                                    minQty: GDetail?.ETItemLowQty,
                                })
                            )
                        );
                    }
                } else {
                    dispatch(setGTicketErr(t('noTHSR.onsubmit.selectTicketTypeQuantity')));
                }
            }

            if (AdvanceTime.length > 0) return;

            let apiData: IValidateItem | undefined;
            if (hasCalendar && calendarErr) {
                console.log('calendar not selected');
                isMobile && dispatch(setScrollTo(scrollToRef.calender));
            } else if (sessionErr) {
                console.log('session not selected');
                isMobile && dispatch(setScrollTo(scrollToRef.session));
            } else if (ticketErr) {
                console.log('ticket not selected');
                isMobile && dispatch(setScrollTo(scrollToRef.ticket));
            } else {
                console.log('validate calendar&session&ticket pass', submitObj);
                // 打驗證API確認當下時間產品上下限數量是否正確
                let order = formatProductDetailOrder(ticketNum);
                const checkValidate: IValidateItem = await parseApiResponse(() =>
                    fetchProductOrderValidity(ETID, order, submitDate)
                );

                apiData = checkValidate;
                dispatch(setVerificationStatus(checkValidate));
                if (checkValidate.IsSuccess) {
                    return {
                        selectChecked: true,
                        apiChecked: checkValidate,
                    };
                }
            }
            return { selectChecked: false, apiChecked: apiData ? apiData : {} };
        case 'redirect':
            let paramURL = formatPassengerURL({
                ETID,
                ticketNum,
                pickCalendar: submitDate,
                pickSession: session.Session,
                Foreign,
                UUID: session.SessionUUID,
                ProductDescKey
            });
            /**
             *   最終導頁網址(確認是否為IT本地開發，IT本地網域不同)&光箱關閉事件
             *   IT本地&F2E本地在判斷環境上不相同(意謂IT本地在process.env.SERVER !== dev)
             *   https://udeveloper.liontravel.com:44369/passenger${paramURL}
             **/
            const finalUrl =
                window.location.hostname === 'udeveloper.liontravel.com'
                    ? `${window.location.origin}/order/passenger${paramURL}`
                    : `${submitURL}${paramURL}`;
            // console.log('paramURL', finalUrl);
            const closeMemberFuc = () => {
                dispatch(setIsSubmitSuccess(false));
            };
            //儲存IT認證參數
            manageSessionStorage('save', 'OrderProcess', 'FromDetail');

            // 確認登入狀態，未登入的話呼叫會員光箱
            if (isLogin) {
                window.location.href = finalUrl;
            } else {
                if (process.env.SERVER === 'dev' || isErpOrder) {
                    // 本機(沒有辦法呼叫會員光箱)或有電話單直接跳轉
                    window.location.href = finalUrl;
                } else {
                    // IT本地、測試&正式機(沒電話單情況)呼叫會員光箱跳轉
                    openMemberLightBox(finalUrl, closeMemberFuc);
                }
            }
            break;
    }
};
export default onSubmit;
